import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    PercentageOutlined,
    CalendarOutlined,
    BankOutlined,
    ReloadOutlined,
    ExclamationCircleFilled,
    UploadOutlined,
    FolderOutlined,
    RightOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    DatePicker,
    Select,
    Card,
    Table,
    Tag,
    Space,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Row,
    Col,
    Statistic,
    Alert,
    Tooltip
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import SideBar from '../../../../components/SideBar';
import NavBar from '../../../../components/NavBar';
import axiosInstance from '../../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../../variables/constants';
import Footer from '../../../../components/Footer';
import ClesRepartitionImport from './ClesRepartitionImport';

const { confirm } = Modal;
const { RangePicker } = DatePicker;

dayjs.extend(isBetween);

const ClesRepartition = () => {
    const [cles, setCles] = useState([]);
    const [branches, setBranches] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [exercices, setExercices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCle, setEditingCle] = useState(null);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [expandedExercices, setExpandedExercices] = useState([]);
    const [expandedBranches, setExpandedBranches] = useState([]);
    const [form] = Form.useForm();

    // Filtres
    const [filtreBranche, setFiltreBranche] = useState(null);
    const [filtreCompagnie, setFiltreCompagnie] = useState(null);
    const [filtreExercice, setFiltreExercice] = useState(null);

    const isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

    useEffect(() => {
        chargerDonneesInitiales();
    }, []);

    useEffect(() => {
        chargerCles();
    }, [filtreBranche, filtreCompagnie, filtreExercice]);

    // Regrouper les clés par exercice puis par branche
    const clesGroupees = useMemo(() => {
        const groupedByExercice = {};

        cles.forEach(cle => {
            const exerciceId = cle?.exercice?.exerciceId;
            const exerciceLabel = cle?.exercice?.libelleExercice || 'Non défini';
            const brancheCode = cle?.branche?.codeBranche;
            const brancheNom = cle?.branche?.nom || cle?.branche?.codeBranche || 'Non défini';

            if (!groupedByExercice[exerciceId]) {
                groupedByExercice[exerciceId] = {
                    exerciceId,
                    exerciceLabel,
                    branches: {},
                    totalCles: 0
                };
            }

            if (!groupedByExercice[exerciceId].branches[brancheCode]) {
                groupedByExercice[exerciceId].branches[brancheCode] = {
                    brancheCode,
                    brancheNom,
                    cles: []
                };
            }

            groupedByExercice[exerciceId].branches[brancheCode].cles.push(cle);
            groupedByExercice[exerciceId].totalCles++;
        });

        return Object.values(groupedByExercice).sort((a, b) =>
            (b.exerciceLabel || '').localeCompare(a.exerciceLabel || '')
        );
    }, [cles]);

    // Initialiser les exercices et branches expandés
    useEffect(() => {
        if (clesGroupees.length > 0 && expandedExercices.length === 0) {
            // Ouvrir le premier exercice par défaut
            setExpandedExercices([clesGroupees[0]?.exerciceId]);
        }
    }, [clesGroupees]);

    const chargerDonneesInitiales = async () => {
        try {
            const [brancheRes, compRes, exRes] = await Promise.all([
                axiosInstance.get('/api/branche'),
                axiosInstance.get('/api/organisme/assurance'),
                axiosInstance.get('/api/exercice')
            ]);

            setBranches(brancheRes.data);
            setCompagnies(compRes.data);
            setExercices(exRes.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des données");
        }
    };

    const chargerCles = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filtreBranche) params.branche = filtreBranche;
            if (filtreCompagnie) params.compagnie = filtreCompagnie;
            if (filtreExercice) params.exercice = filtreExercice;

            const response = await axiosInstance.get('/api/cle-repartition', { params });
            setCles(response.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des clés de répartition");
        } finally {
            setLoading(false);
        }
    };

    const ouvrirModalCreation = () => {
        setEditingCle(null);
        form.resetFields();
        setModalVisible(true);
    };

    const ouvrirModalEdition = (cle) => {
        setEditingCle(cle);
        form.setFieldsValue({
            compagnie: cle?.compagnie?.code,
            branche: cle?.branche?.codeBranche,
            exercice: cle?.exercice?.exerciceId,
            cle: cle.cle,
            periode: [dayjs(cle?.dateDebut), dayjs(cle?.dateFin)],
            libelle: cle?.commentaire
        });
        setModalVisible(true);
    };

    const supprimerCle = (cle) => {
        confirm({
            title: 'Confirmer la suppression',
            icon: <ExclamationCircleFilled />,
            content: `Voulez-vous vraiment supprimer la clé de répartition pour ${cle.compagnie.raisonSociale} ?`,
            okText: 'Supprimer',
            okType: 'danger',
            cancelText: 'Annuler',
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/api/cle-repartition/${cle.cleRepartitionId}`);
                    toast.success("Clé de répartition supprimée");
                    chargerCles();
                } catch (err) {
                    console.error(err);
                    toast.error("Erreur lors de la suppression");
                }
            },
        });
    };

    const validerFormulaire = async (values) => {
        try {
            const cleData = {
                compagnie: values.compagnie,
                branche: values.branche,
                exercice: values.exercice,
                cle: values.cle,
                dateDebut: values.periode[0].format('YYYY-MM-DD'),
                dateFin: values.periode[1].format('YYYY-MM-DD'),
                commentaire: values.libelle,
                utilisateur: LOGGED_USER,
                cleRepartitionId: editingCle?.cleRepartitionId
            };

            console.log('Données de la clé à soumettre:', cleData);

            if (editingCle) {
                await axiosInstance.put(`/api/cle-repartition`, cleData);
                toast.success("Clé de répartition modifiée");
            } else {
                await axiosInstance.post('/api/cle-repartition', cleData);
                toast.success("Clé de répartition créée");
            }

            setModalVisible(false);
            form.resetFields();
            chargerCles();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Erreur lors de l'enregistrement");
        }
    };

    const verifierChevauchement = async (values) => {
        try {
            const response = await axiosInstance.post('/api/cle-repartition/verifier-chevauchement', {
                compagnieCode: values.compagnie,
                brancheCode: values.branche,
                dateDebut: values.periode[0].format('YYYY-MM-DD'),
                dateFin: values.periode[1].format('YYYY-MM-DD'),
                cleRepartitionId: editingCle?.cleRepartitionId
            });

            if (response.data.chevauchement) {
                toast.warning("Une clé de répartition existe déjà pour cette période");
                return false;
            }
            return true;
        } catch (err) {
            console.error(err);
            return true;
        }
    };

    const calculerStatistiques = () => {
        const totalCles = cles.length;
        const compagniesUniques = new Set(cles.map(c => c.compagnie.code)).size;
        const tauxMoyen = cles.length > 0
            ? cles.reduce((sum, c) => sum + c.cle, 0) / cles.length
            : 0;
        const clesActives = cles.filter(c => {
            const maintenant = dayjs();
            return maintenant.isBetween(dayjs(c.dateDebut), dayjs(c.dateFin), null, '[]');
        }).length;

        return { totalCles, compagniesUniques, tauxMoyen, clesActives };
    };

    const toggleExercice = (exerciceId) => {
        setExpandedExercices(prev =>
            prev.includes(exerciceId)
                ? prev.filter(id => id !== exerciceId)
                : [...prev, exerciceId]
        );
    };

    const toggleBranche = (key) => {
        setExpandedBranches(prev =>
            prev.includes(key)
                ? prev.filter(k => k !== key)
                : [...prev, key]
        );
    };

    const expandAll = () => {
        const allExerciceIds = clesGroupees.map(g => g.exerciceId);
        const allBrancheKeys = clesGroupees.flatMap(g =>
            Object.keys(g.branches).map(b => `${g.exerciceId}-${b}`)
        );
        setExpandedExercices(allExerciceIds);
        setExpandedBranches(allBrancheKeys);
    };

    const collapseAll = () => {
        setExpandedExercices([]);
        setExpandedBranches([]);
    };

    // Colonnes pour le tableau des clés (sans exercice et branche car déjà groupés)
    const colonnesCles = [
        {
            title: 'Compagnie',
            dataIndex: 'compagnie',
            key: 'compagnie',
            render: (compagnie) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{compagnie.raisonSociale}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{compagnie.code}</div>
                </div>
            ),
            sorter: (a, b) => a.compagnie.raisonSociale.localeCompare(b.compagnie.raisonSociale),
        },
        {
            title: 'Taux',
            dataIndex: 'cle',
            key: 'cle',
            render: (cle) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <PercentageOutlined style={{ color: '#242166' }} />
                    <span style={{ fontWeight: 600, fontSize: '16px', color: '#242166' }}>
                        {cle}%
                    </span>
                </div>
            ),
            align: 'center',
            width: 120,
            sorter: (a, b) => a.cle - b.cle,
        },
        {
            title: 'Période de validité',
            key: 'periode',
            render: (_, record) => (
                <div>
                    <div style={{ fontSize: '12px' }}>
                        <strong>Début:</strong> {dayjs(record.dateDebut).format('DD/MM/YYYY')}
                    </div>
                    <div style={{ fontSize: '12px' }}>
                        <strong>Fin:</strong> {dayjs(record.dateFin).format('DD/MM/YYYY')}
                    </div>
                </div>
            ),
            width: 180,
        },
        {
            title: 'Statut',
            key: 'statut',
            width: 100,
            render: (_, record) => {
                const maintenant = dayjs();
                const dateDebut = dayjs(record.dateDebut);
                const dateFin = dayjs(record.dateFin);

                if (maintenant.isBefore(dateDebut)) {
                    return <Tag color="default">À venir</Tag>;
                } else if (maintenant.isAfter(dateFin)) {
                    return <Tag color="error">Expirée</Tag>;
                } else {
                    return <Tag color="success">Active</Tag>;
                }
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Modifier">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => ouvrirModalEdition(record)}
                            disabled={!isPoolUser}
                        />
                    </Tooltip>
                    <Tooltip title="Supprimer">
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => supprimerCle(record)}
                            disabled={!isPoolUser}
                        />
                    </Tooltip>
                </Space>
            ),
            align: 'center',
            width: 100,
        },
    ];

    const stats = calculerStatistiques();

    const ouvrirModalImport = () => {
        setImportModalVisible(true);
    };

    const fermerModalImport = () => {
        setImportModalVisible(false);
    };

    const onImportSuccess = () => {
        chargerCles();
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <style>{`
                .cles-header-card {
                    background: linear-gradient(135deg, #242166 0%, #1a1850 100%);
                    border-radius: 16px;
                    padding: 32px;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 20px rgba(36, 33, 102, 0.25);
                    color: white;
                }

                .cles-header-card h2 {
                    color: white;
                    font-size: 28px;
                    font-weight: 700;
                    margin: 0 0 8px 0;
                }

                .filter-card {
                    background: white;
                    border-radius: 12px;
                    padding: 20px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    margin-bottom: 24px;
                }

                .stats-card {
                    background: white;
                    border-radius: 12px;
                    padding: 24px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    margin-bottom: 24px;
                }

                .exercice-group {
                    background: white;
                    border-radius: 12px;
                    margin-bottom: 16px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                }

                .exercice-header {
                    background: linear-gradient(135deg, #ebeaea 0%, #e3e0e0 100%);
                    color: white;
                    padding: 16px 20px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    transition: all 0.3s;
                }

                .exercice-header:hover {
                    background: linear-gradient(135deg, #e5e5e5 0%, #bdbdbd 100%);
                }

                .exercice-header-content {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .exercice-header h3 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: black;
                }

                .exercice-badge {
                    background: rgba(0, 0, 0, 0.2);
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 13px;
                }

                .branche-group {
                    border-left: 4px solid #f0f0f0;
                    margin: 0;
                }

                .branche-header {
                    background: #fafafa;
                    padding: 12px 20px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid #f0f0f0;
                    transition: all 0.2s;
                }

                .branche-header:hover {
                    background: #f5f5f5;
                }

                .branche-header-content {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .branche-header h4 {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 500;
                    color: #333;
                }

                .branche-badge {
                    background: #fff7e6;
                    color: #fa8c16;
                    padding: 2px 10px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .cles-table-wrapper {
                    padding: 0;
                }

                .cles-table-wrapper .ant-table {
                    border-radius: 0;
                }

                .cles-table-wrapper .ant-table-thead > tr > th {
                    background: #f9f9f9;
                }

                .expand-icon {
                    transition: transform 0.3s;
                }

                .expand-icon.expanded {
                    transform: rotate(90deg);
                }

                .empty-group {
                    padding: 40px;
                    text-align: center;
                    color: #999;
                }
            `}</style>

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f5f5f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div className="cles-header-card">
                                <h2>
                                    <PercentageOutlined /> Clés de Répartition
                                </h2>
                                <p>Gestion des clés de répartition par compagnie, catégorie et exercice</p>
                            </div>

                            {/* Statistiques */}
                            <div className="stats-card">
                                <Row gutter={16}>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Total Clés"
                                            value={stats.totalCles}
                                            prefix={<PercentageOutlined />}
                                            valueStyle={{ color: '#242166' }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Compagnies"
                                            value={stats.compagniesUniques}
                                            prefix={<BankOutlined />}
                                            valueStyle={{ color: '#242166' }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Taux Moyen"
                                            value={stats.tauxMoyen.toFixed(2)}
                                            suffix="%"
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <Statistic
                                            title="Clés Actives"
                                            value={stats.clesActives}
                                            valueStyle={{ color: '#faad14' }}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            {/* Filtres */}
                            <div className="filter-card">
                                <Row gutter={16}>
                                    <Col xs={24} sm={8} md={5}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            <SearchOutlined /> Catégorie
                                        </label>
                                        <Select
                                            placeholder="Toutes les catégories"
                                            style={{ width: '100%' }}
                                            onChange={setFiltreBranche}
                                            allowClear
                                            options={branches?.map(c => ({
                                                value: c.codeBranche,
                                                label: c.nom
                                            }))}
                                        />
                                    </Col>

                                    <Col xs={24} sm={8} md={5}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            <BankOutlined /> Compagnie
                                        </label>
                                        <Select
                                            placeholder="Toutes les compagnies"
                                            style={{ width: '100%' }}
                                            onChange={setFiltreCompagnie}
                                            allowClear
                                            options={compagnies.map(c => ({
                                                value: c.code,
                                                label: c.raisonSociale
                                            }))}
                                        />
                                    </Col>

                                    <Col xs={24} sm={8} md={5}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            <CalendarOutlined /> Exercice
                                        </label>
                                        <Select
                                            placeholder="Tous les exercices"
                                            style={{ width: '100%' }}
                                            onChange={setFiltreExercice}
                                            allowClear
                                            options={exercices.map(e => ({
                                                value: e?.exerciceId,
                                                label: e?.libelleExercice
                                            }))}
                                        />
                                    </Col>

                                    <Col xs={24} sm={24} md={9}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            &nbsp;
                                        </label>
                                        <Space style={{ width: '100%', flexWrap: 'wrap' }}>
                                            <Button
                                                type="primary"
                                                icon={<ReloadOutlined />}
                                                onClick={chargerCles}
                                                loading={loading}
                                            >
                                                Actualiser
                                            </Button>
                                            <Button onClick={expandAll}>
                                                Tout déplier
                                            </Button>
                                            <Button onClick={collapseAll}>
                                                Tout replier
                                            </Button>
                                            {isPoolUser && (
                                                <>
                                                    <Button
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        onClick={ouvrirModalCreation}
                                                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                                    >
                                                        Nouvelle clé
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        icon={<UploadOutlined />}
                                                        onClick={ouvrirModalImport}
                                                        style={{ background: '#242166', borderColor: '#242166' }}
                                                    >
                                                        Importer
                                                    </Button>
                                                </>
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </div>

                            {/* Liste groupée */}
                            <ConfigProvider locale={frFR}>
                                {loading ? (
                                    <Card loading={true} style={{ borderRadius: '12px' }} />
                                ) : clesGroupees.length === 0 ? (
                                    <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
                                        <div className="empty-group">
                                            <PercentageOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
                                            <p>Aucune clé de répartition trouvée</p>
                                        </div>
                                    </Card>
                                ) : (
                                    clesGroupees.map(exerciceGroup => (
                                        <div key={exerciceGroup.exerciceId} className="exercice-group">
                                            {/* En-tête Exercice */}
                                            <div
                                                className="exercice-header"
                                                onClick={() => toggleExercice(exerciceGroup.exerciceId)}
                                            >
                                                <div className="exercice-header-content">
                                                    <RightOutlined
                                                        className={`expand-icon ${expandedExercices.includes(exerciceGroup.exerciceId) ? 'expanded' : ''}`}
                                                    />
                                                    <CalendarOutlined />
                                                    <h3>{exerciceGroup.exerciceLabel}</h3>
                                                    <span className="exercice-badge">
                                                        {exerciceGroup.totalCles} clé(s)
                                                    </span>
                                                </div>
                                                <span className="exercice-badge">
                                                    {Object.keys(exerciceGroup.branches).length} branche(s)
                                                </span>
                                            </div>

                                            {/* Contenu Exercice */}
                                            {expandedExercices.includes(exerciceGroup.exerciceId) && (
                                                <div>
                                                    {Object.values(exerciceGroup.branches).map(brancheGroup => {
                                                        const brancheKey = `${exerciceGroup.exerciceId}-${brancheGroup.brancheCode}`;
                                                        return (
                                                            <div key={brancheKey} className="branche-group">
                                                                {/* En-tête Branche */}
                                                                <div
                                                                    className="branche-header"
                                                                    onClick={() => toggleBranche(brancheKey)}
                                                                >
                                                                    <div className="branche-header-content">
                                                                        <RightOutlined
                                                                            className={`expand-icon ${expandedBranches.includes(brancheKey) ? 'expanded' : ''}`}
                                                                            style={{ fontSize: 12 }}
                                                                        />
                                                                        <FolderOutlined style={{ color: '#fa8c16' }} />
                                                                        <h4>{brancheGroup.brancheNom}</h4>
                                                                        <Tag color="orange">{brancheGroup.brancheCode}</Tag>
                                                                    </div>
                                                                    <span className="branche-badge">
                                                                        {brancheGroup.cles.length} clé(s)
                                                                    </span>
                                                                </div>

                                                                {/* Table des clés */}
                                                                {expandedBranches.includes(brancheKey) && (
                                                                    <div className="cles-table-wrapper">
                                                                        <Table
                                                                            columns={colonnesCles}
                                                                            dataSource={brancheGroup.cles}
                                                                            rowKey="cleRepartitionId"
                                                                            pagination={false}
                                                                            size="small"
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                )}
                            </ConfigProvider>

                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal de création/édition */}
            <Modal
                title={
                    <span>
                        <PercentageOutlined style={{ marginRight: 8 }} />
                        {editingCle ? 'Modifier la clé de répartition' : 'Nouvelle clé de répartition'}
                    </span>
                }
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={700}
            >
                <Alert
                    message="Information importante"
                    description="Les clés de répartition déterminent comment les montants sont répartis entre les compagnies lors de la rétrocession. Assurez-vous que les périodes ne se chevauchent pas pour une même compagnie et catégorie."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={async (values) => {
                        const chevauchementOk = await verifierChevauchement(values);
                        if (chevauchementOk) {
                            validerFormulaire(values);
                        }
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Compagnie"
                                name="compagnie"
                                rules={[{ required: true, message: 'Veuillez sélectionner une compagnie' }]}
                            >
                                <Select
                                    placeholder="Sélectionner une compagnie"
                                    showSearch
                                    optionFilterProp="children"
                                    options={compagnies.map(c => ({
                                        value: c.code,
                                        label: c.raisonSociale
                                    }))}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Branche"
                                name="branche"
                                rules={[{ required: true, message: 'Veuillez sélectionner une branche' }]}
                            >
                                <Select
                                    placeholder="Sélectionner une branche"
                                    options={branches?.map(c => ({
                                        value: c.codeBranche,
                                        label: c.nom
                                    }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Exercice"
                                name="exercice"
                                rules={[{ required: true, message: 'Veuillez sélectionner un exercice' }]}
                            >
                                <Select
                                    placeholder="Sélectionner un exercice"
                                    options={exercices.map(e => ({
                                        value: e.exerciceId,
                                        label: e.libelleExercice
                                    }))}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label="Taux de répartition (%)"
                                name="cle"
                                rules={[
                                    { required: true, message: 'Veuillez saisir la clé' },
                                    { type: 'number', min: 0, max: 100, message: 'La clé doit être entre 0 et 100' }
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Ex: 25"
                                    min={0}
                                    max={100}
                                    step={0.01}
                                    precision={2}
                                    addonAfter="%"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        label="Période de validité"
                        name="periode"
                        rules={[{ required: true, message: 'Veuillez sélectionner la période' }]}
                    >
                        <RangePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            placeholder={['Date de début', 'Date de fin']}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Libellé / Description"
                        name="libelle"
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder="Description optionnelle de la clé de répartition"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => {
                                setModalVisible(false);
                                form.resetFields();
                            }}>
                                Annuler
                            </Button>
                            <Button type="primary" htmlType="submit">
                                {editingCle ? 'Modifier' : 'Créer'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
            <ClesRepartitionImport
                visible={importModalVisible}
                onClose={fermerModalImport}
                onImportSuccess={onImportSuccess}
            />
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default ClesRepartition;